import { NavLink, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Inbox, LayoutDashboard, Library, CheckSquare, KanbanSquare,
  ShieldCheck, ScrollText, LogOut, Moon, Sun, Radio, Sparkles, KeyRound,
} from "lucide-react";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-item-dashboard" },
  { to: "/triage", label: "Triage Inbox", icon: Inbox, testid: "nav-item-triage-inbox", badge: "triage" },
  { to: "/assets", label: "Asset Bank", icon: Library, testid: "nav-item-asset-bank" },
  { to: "/approvals", label: "Approvals", icon: CheckSquare, testid: "nav-item-approval-queue", badge: "approvals" },
  { to: "/crm", label: "CRM Pipeline", icon: KanbanSquare, testid: "nav-item-crm-pipeline" },
  { to: "/compliance", label: "Compliance", icon: ShieldCheck, testid: "nav-item-suppression-compliance" },
  { to: "/audit", label: "Audit Trail", icon: ScrollText, testid: "nav-item-audit" },
];

export function Layout({ children }) {
  const { user, logout, theme, toggleTheme } = useAuth();
  const navigate = useNavigate();

  const { data: opps = [] } = useQuery({ queryKey: ["opps-nav"], queryFn: async () => (await api.get("/opportunities")).data, refetchInterval: 60000 });
  const { data: outreach = [] } = useQuery({ queryKey: ["q-outreach-nav"], queryFn: async () => (await api.get("/approvals/queue?kind=outreach")).data });
  const { data: submission = [] } = useQuery({ queryKey: ["q-submission-nav"], queryFn: async () => (await api.get("/approvals/queue?kind=submission")).data });

  const counts = {
    triage: opps.filter((o) => o.status === "new").length,
    approvals: outreach.length + submission.length,
  };

  const handleLogout = async () => { await logout(); navigate("/login"); };

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      <aside className="w-[240px] shrink-0 border-r border-border flex flex-col bg-secondary/40">
        <div className="px-5 py-5 border-b border-border">
          <div className="flex items-center gap-2.5">
            <div className="grid h-9 w-9 place-items-center rounded-lg bg-primary/15 border border-primary/30">
              <Radio className="h-5 w-5 text-primary" />
            </div>
            <div>
              <div className="font-heading font-bold text-sm leading-tight">Casey's Cadence</div>
              <div className="text-[11px] text-muted-foreground">Booking Engine</div>
            </div>
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
          {NAV.map(({ to, label, icon: Icon, testid, badge }) => {
            const count = badge ? counts[badge] : 0;
            return (
              <NavLink
                key={to}
                to={to}
                data-testid={testid}
                className={({ isActive }) =>
                  `group flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                    isActive ? "bg-primary/15 text-primary" : "text-muted-foreground hover:text-foreground hover:bg-accent"
                  }`
                }
              >
                <Icon className="h-4 w-4 shrink-0" />
                <span className="flex-1">{label}</span>
                {count > 0 && (
                  <span className="rounded-full bg-primary/20 text-primary text-[10px] font-mono font-semibold px-1.5 py-0.5 min-w-[18px] text-center">
                    {count}
                  </span>
                )}
              </NavLink>
            );
          })}
        </nav>

        <div className="border-t border-border p-3 space-y-2">
          <AiModelPicker />
          <div className="flex items-center justify-between rounded-lg px-2 py-1.5">
            <div className="min-w-0">
              <div className="text-xs font-medium truncate">{user?.name || "Operator"}</div>
              <div className="text-[11px] text-muted-foreground truncate">{user?.email}</div>
            </div>
            <Button variant="ghost" size="icon" onClick={toggleTheme} data-testid="theme-toggle-btn" className="h-8 w-8 shrink-0">
              {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </Button>
          </div>
          <Button variant="outline" size="sm" onClick={handleLogout} data-testid="logout-btn" className="w-full justify-start gap-2">
            <LogOut className="h-4 w-4" /> Sign out
          </Button>
          <SetPasswordDialog />
        </div>
      </aside>

      <main className="flex-1 overflow-y-auto">{children}</main>
    </div>
  );
}

function AiModelPicker() {  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["ai-models"], queryFn: async () => (await api.get("/ai/models")).data });
  const save = useMutation({
    mutationFn: async (value) => {
      const [ai_provider, ai_model] = value.split("|");
      return (await api.put("/settings", { ai_provider, ai_model })).data;
    },
    onSuccess: (d) => { toast.success("AI model updated"); qc.invalidateQueries({ queryKey: ["ai-models"] }); },
  });

  if (!data) return null;
  const current = `${data.ai_provider}|${data.ai_model}`;

  return (
    <div className="rounded-lg border border-border bg-card/60 p-2.5" data-testid="ai-model-picker">
      <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground mb-1.5">
        <Sparkles className="h-3 w-3 text-primary" /> AI drafting model
      </div>
      <Select value={current} onValueChange={(v) => save.mutate(v)}>
        <SelectTrigger className="h-8 text-xs" data-testid="ai-model-select"><SelectValue /></SelectTrigger>
        <SelectContent>
          {Object.entries(data.catalog).map(([provider, group]) => (
            <SelectGroup key={provider}>
              <SelectLabel>{group.label}</SelectLabel>
              {group.models.map((m) => (
                <SelectItem key={m.id} value={`${provider}|${m.id}`} data-testid={`ai-option-${m.id}`}>{m.label}</SelectItem>
              ))}
            </SelectGroup>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function SetPasswordDialog() {
  const { setPassword, user } = useAuth();
  const [open, setOpen] = useState(false);
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (pw.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    if (pw !== confirm) { toast.error("Passwords do not match"); return; }
    setBusy(true);
    try {
      await setPassword(pw);
      toast.success("Email password set — you can now sign in with your email");
      setOpen(false); setPw(""); setConfirm("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not set password");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" className="w-full justify-start gap-2 text-muted-foreground" data-testid="set-password-btn">
          <KeyRound className="h-4 w-4" /> Set email password
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><KeyRound className="h-4 w-4 text-primary" /> Set email password</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">Set a password for <span className="font-medium text-foreground">{user?.email}</span> so you can sign in with your email (in addition to Google).</p>
        <div className="space-y-3 pt-1">
          <div className="space-y-1.5"><Label>New password</Label><Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="At least 8 characters" data-testid="set-password-new" /></div>
          <div className="space-y-1.5"><Label>Confirm password</Label><Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} onKeyDown={(e) => e.key === "Enter" && submit()} data-testid="set-password-confirm" /></div>
        </div>
        <DialogFooter><Button disabled={busy || !pw || !confirm} onClick={submit} data-testid="set-password-save">{busy ? "Saving…" : "Save password"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

